import * as Sentry from '@sentry/browser'
import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { hardRefresh } from 'utils'

interface VersionResponse {
  version: string
}

export const AppUpdater = () => {
  const lastCheckedVersion = useRef<string | null>(null)

  const versionEndpoint =
    process.env.NODE_ENV === 'production'
      ? '/version'
      : `//localhost:${window._env_.API_PORT}/version`

  const uiVersion = window._env_.BUILD_VERSION

  const fetchBackendVersion = useCallback(async (): Promise<string | null> => {
    let response
    try {
      response = await fetch(versionEndpoint)
    } catch {
      return null
    }

    try {
      const data = (await response.json()) as VersionResponse
      return data.version
    } catch (error) {
      Sentry.captureException(`Failed to parse version response: ${error}`)
      return null
    }
  }, [versionEndpoint])

  const shouldShowUpdateToast = useCallback(
    (backendVersion: string | null): boolean => {
      if (!backendVersion) return false
      return (
        backendVersion !== 'unknown' &&
        backendVersion !== uiVersion &&
        backendVersion !== lastCheckedVersion.current
      )
    },
    [uiVersion],
  )

  const showUpdateToast = useCallback((backendVersion: string) => {
    lastCheckedVersion.current = backendVersion

    toast('Update available', {
      description: 'A new Portal version is available',
      duration: 999999999,
      closeButton: true,
      action: {
        label: 'Update',
        onClick: async () => await hardRefresh(window.location.href),
      },
    })
  }, [])

  const checkVersion = useCallback(async () => {
    const backendVersion = await fetchBackendVersion()

    if (backendVersion && shouldShowUpdateToast(backendVersion)) {
      showUpdateToast(backendVersion)
    }
  }, [fetchBackendVersion, shouldShowUpdateToast, showUpdateToast])

  useEffect(() => {
    checkVersion() // Run immediately on mount

    const interval = setInterval(checkVersion, 1000 * 60) // 1 minute
    return () => clearInterval(interval)
  }, [checkVersion])

  return null
}
